import { Injectable } from '@angular/core';

import { jobHiringManagerCandidatesLoad } from '@modules/job-page/store/hiring-manager';
import { NoteFactory } from '@modules/notes/note-factory/note-factory';
import { notesCreate } from '@modules/notes/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SuccessAlert } from '@shared/alert';
import { Association, CandidateJobStatus } from '@shared/models';
import { HiringManagerStatus } from '@shared/models/hiring-manager-status';
import { HiringManagerService } from '@shared/services/hiring-manager.service';

import { MojoBatchService } from 'mojo-batch';
import { NoteSubject } from 'mojo-irp-notes';
import { of, from } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
  advanceHiringManagerCandidateAction,
  assignCandidateToHiringManagerFail,
  conductHiringManagerCandidateActionSuccess,
  loadCandidateHiringManagerStatus,
  loadCandidateHiringManagerStatusSuccess,
  rejectHiringManagerCandidateAction,
  submitHiringManagerCandidateAction,
  submitBatchHiringManagerCandidateAction,
  rejectBatchHiringManagerCandidateAction,
  advanceBatchHiringManagerCandidateAction,
} from './hiring-manager-status.actions';

@Injectable()
export class HiringManagerStatusEffects {
  submitHiringManagerCandidateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitHiringManagerCandidateAction),
      mergeMap(({ candidateId, jobId, associationId, noteComment, isBatchOperation }) =>
        this.conductHiringManagerCandidateAction(
          'submit',
          candidateId,
          jobId,
          noteComment,
          associationId,
          true,
          isBatchOperation,
        ),
      ),
    ),
  );

  submitBatchHiringManagerCandidateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitBatchHiringManagerCandidateAction),
      mergeMap(({ jobId, associations, noteComment }) =>
        this.conductHiringManagerCandidateActionBulk(
          'submit-many',
          jobId,
          noteComment,
          associations,
          true,
        ),
      ),
    ),
  );

  rejectHiringManagerCandidateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rejectHiringManagerCandidateAction),
      mergeMap(
        ({
          candidateId,
          jobId,
          associationId,
          noteComment,
          showSnackbar = false,
          isBatchOperation = false,
        }) =>
          this.conductHiringManagerCandidateAction(
            'reject',
            candidateId,
            jobId,
            noteComment,
            associationId,
            showSnackbar,
            isBatchOperation,
          ),
      ),
    ),
  );

  rejectBatchHiringManagerCandidateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rejectBatchHiringManagerCandidateAction),
      mergeMap(({ jobId, associations, noteComment }) =>
        this.conductHiringManagerCandidateActionBulk(
          'reject-many',
          jobId,
          noteComment,
          associations,
          true,
        ),
      ),
    ),
  );

  advanceHiringManagerCandidateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(advanceHiringManagerCandidateAction),
      mergeMap(
        ({
          candidateId,
          jobId,
          associationId,
          noteComment,
          showSnackbar = false,
          isBatchOperation = false,
        }) =>
          this.conductHiringManagerCandidateAction(
            'advance',
            candidateId,
            jobId,
            noteComment,
            associationId,
            showSnackbar,
            isBatchOperation,
          ),
      ),
    ),
  );

  advanceBatchHiringManagerCandidateAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(advanceBatchHiringManagerCandidateAction),
      mergeMap(({ jobId, associations, noteComment }) =>
        this.conductHiringManagerCandidateActionBulk(
          'advance-many',
          jobId,
          noteComment,
          associations,
          true,
        ),
      ),
    ),
  );

  private conductHiringManagerCandidateAction(
    url,
    candidateId,
    jobId,
    noteComment,
    associationId,
    showSnackbar = true,
    isBatchOperation = false,
  ) {
    return this.hiringManagerStatusService.post({ url, body: { candidateId, jobId } }).pipe(
      map((response: CandidateJobStatus) =>
        conductHiringManagerCandidateActionSuccess({
          hiringManagerStatus: response,
          noteComment,
          associationId,
          showSnackbar,
          isBatchOperation,
        }),
      ),
      catchError((error) => of(assignCandidateToHiringManagerFail({ error }))),
    );
  }

  private conductHiringManagerCandidateActionBulk(
    url,
    jobId,
    noteComment,
    associations: Association[],
    showSnackbar = true,
  ) {
    const body = associations.map((a) => ({
      candidateId: a.candidateId,
      jobId,
    }));
    return this.hiringManagerStatusService.post({ url, body }).pipe(
      mergeMap((response: CandidateJobStatus[]) => {
        const byKey = new Map(associations.map((a) => [`${a.candidateId}|${a.reqId}`, a.id]));
        return from(response).pipe(
          map((item) => {
            const key = `${item.candidateId}|${item.jobId}`;
            return conductHiringManagerCandidateActionSuccess({
              hiringManagerStatus: item,
              noteComment,
              associationId: byKey.get(key),
              showSnackbar,
              isBatchOperation: true,
            });
          }),
        );
      }),
      catchError((error) => of(assignCandidateToHiringManagerFail({ error }))),
    );
  }

  conductHiringManagerCandidateActionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(conductHiringManagerCandidateActionSuccess),
      mergeMap(
        ({ hiringManagerStatus, noteComment, associationId, showSnackbar, isBatchOperation }) => {
          const actions = [];
          if (
            hiringManagerStatus.status === HiringManagerStatus.ADVANCED ||
            hiringManagerStatus.status === HiringManagerStatus.REJECTED
          ) {
            actions.push(
              jobHiringManagerCandidatesLoad({
                params: { offset: 0 },
              }),
            );
          }

          if (showSnackbar) {
            if (isBatchOperation) {
              this.batchSelectionManagerService.clearSelection();
              actions.push(
                new SuccessAlert(
                  `Those candidates have been ${hiringManagerStatus.status.toLowerCase()}.`,
                ),
              );
            } else {
              actions.push(
                new SuccessAlert(
                  `The candidate has been ${hiringManagerStatus.status.toLowerCase()}.`,
                ),
              );
            }
          }

          return [
            ...actions,
            notesCreate({
              payload: this.noteFactory.create(
                {
                  id: associationId,
                  status: hiringManagerStatus.status,
                  noteComment,
                } as Association,
                NoteSubject.AssociationHiringManager,
              ),
            }),
          ];
        },
      ),
    ),
  );

  loadCandidateHiringManagerStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCandidateHiringManagerStatus),
      mergeMap(({ jobId, candidatesIds }) => {
        const sortedCandidatesIds = Array.from(new Set(candidatesIds.map((x) => String(x)).sort()));
        return this.hiringManagerStatusService.getStatuses(jobId, sortedCandidatesIds).pipe(
          map((statuses) =>
            loadCandidateHiringManagerStatusSuccess({
              statuses,
            }),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private hiringManagerStatusService: HiringManagerService,
    private noteFactory: NoteFactory,
    private batchSelectionManagerService: MojoBatchService,
  ) {}
}
